// Draft editor for a single proposal package (playlist of library pages).

import {
  PROPOSAL_PAGE_KIND_LABELS,
  type ProposalAlternatePage,
  type ProposalBuiltinPageKind,
} from "@/lib/opportunity/proposalAlternateTypes";
import { normalizeAlternatePages } from "@/lib/opportunity/proposalAlternateApply";
import type { ProposalPage } from "@/lib/opportunity/proposalPageTypes";
import { ProposalAlternateDocPagesEditor } from "./ProposalAlternateDocPagesEditor";

type Props = {
  draftKey: string;
  draftLabel: string;
  draftPages: ProposalAlternatePage[];
  editingKey: string;
  libraryPages: ProposalPage[];
  docPagesSupported: boolean;
  onDraftKeyChange: (v: string) => void;
  onDraftLabelChange: (v: string) => void;
  onDraftPagesChange: (pages: ProposalAlternatePage[]) => void;
  onTogglePage: (pageKind: ProposalBuiltinPageKind) => void;
  onSetPageLibraryId: (pageKind: ProposalBuiltinPageKind, proposalPageId: string) => void;
  onMovePage: (pageKind: ProposalBuiltinPageKind, direction: "up" | "down") => void;
  onSave: () => void;
  onPreview?: () => void;
  onCancel: () => void;
};

export function ProposalAlternatePackageEditor(props: Props) {
  const {
    draftKey,
    draftLabel,
    draftPages,
    editingKey,
    libraryPages,
    docPagesSupported,
    onDraftKeyChange,
    onDraftLabelChange,
    onDraftPagesChange,
    onTogglePage,
    onSetPageLibraryId,
    onMovePage,
    onSave,
    onPreview,
    onCancel,
  } = props;

  const sortedDraftPages = normalizeAlternatePages(draftPages).filter(
    (p) => p.page_kind !== "doc_ref",
  );

  return (
    <div className="space-y-3 rounded-lg border border-indigo-200 bg-white p-3 dark:border-indigo-900 dark:bg-zinc-900">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="text-gray-500">Key</span>
          <input
            type="text"
            value={draftKey}
            disabled={editingKey !== "__new__"}
            onChange={(e) => onDraftKeyChange(e.target.value)}
            placeholder="financing"
            className="mt-1 w-full rounded border border-gray-200 px-2 py-1.5 font-mono text-sm disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800"
          />
        </label>
        <label className="block text-xs">
          <span className="text-gray-500">Label</span>
          <input
            type="text"
            value={draftLabel}
            onChange={(e) => onDraftLabelChange(e.target.value)}
            placeholder="Financing package"
            className="mt-1 w-full rounded border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
          />
        </label>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-700 dark:text-zinc-300">Pages</p>
        {sortedDraftPages.map((page, idx) => {
          const pageKind = page.page_kind as ProposalBuiltinPageKind;
          const options = libraryPages.filter((p) => p.archetype === pageKind);
          return (
            <div
              key={pageKind}
              className="flex flex-wrap items-center gap-2 rounded border border-gray-100 px-2 py-1.5 text-xs dark:border-zinc-800"
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={page.included}
                  onChange={() => onTogglePage(pageKind)}
                />
                {PROPOSAL_PAGE_KIND_LABELS[pageKind]}
              </label>
              <select
                value={page.proposal_page_id ?? ""}
                onChange={(e) => onSetPageLibraryId(pageKind, e.target.value)}
                className="min-w-[10rem] flex-1 rounded border border-gray-200 px-1.5 py-1 dark:border-zinc-700 dark:bg-zinc-800"
                aria-label={`${PROPOSAL_PAGE_KIND_LABELS[pageKind]} library page`}
              >
                <option value="">Primary default</option>
                {options.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={idx === 0}
                onClick={() => onMovePage(pageKind, "up")}
                className="rounded border border-gray-300 px-1.5 py-0.5 disabled:opacity-40 dark:border-zinc-600"
              >
                ↑
              </button>
              <button
                type="button"
                disabled={idx === sortedDraftPages.length - 1}
                onClick={() => onMovePage(pageKind, "down")}
                className="rounded border border-gray-300 px-1.5 py-0.5 disabled:opacity-40 dark:border-zinc-600"
              >
                ↓
              </button>
            </div>
          );
        })}
      </div>
      {!docPagesSupported ? (
        <p className="text-[10px] text-amber-700 dark:text-amber-200">
          Attached documents need SQL migration{" "}
          <code className="font-mono">docs/sql/opp_proposal_alternate_doc_pages_v1.sql</code>.
        </p>
      ) : (
        <ProposalAlternateDocPagesEditor draftPages={draftPages} onChange={onDraftPagesChange} />
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSave}
          className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white"
        >
          Save package
        </button>
        {onPreview ? (
          <button
            type="button"
            onClick={onPreview}
            className="rounded border border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
          >
            Preview
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 px-3 py-1.5 text-xs dark:border-zinc-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
